import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  Paper,
  Divider,
  Grid,
  MenuItem,
  InputAdornment,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tabs,
  Tab,
  Avatar,
  CircularProgress,
  Tooltip,
  Rating
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Storefront as StorefrontIcon,
  Restaurant as RestaurantIcon,
  LocationOn as LocationOnIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Description as DescriptionIcon,
  AccessTime as AccessTimeIcon,
  AttachMoney as AttachMoneyIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Map as MapIcon,
  Image as ImageIcon,
  Fastfood as FastfoodIcon,
  Category as CategoryIcon,
  Receipt as ReceiptIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import api from '../services/api';
import ImageUpload from '../components/ImageUpload';
import GoogleMapModal from '../components/GoogleMapModal';

const AdminMenuEdit = ({ keywordsData, language }) => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [restaurants, setRestaurants] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [openMap, setOpenMap] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [restaurantToDelete, setRestaurantToDelete] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0
  });

  const [formData, setFormData] = useState({
    id: null,
    name: '',
    name_en: '',
    description: '',
    description_en: '',
    phone: '',
    email: '',
    address: '',
    address_en: '',
    city: '',
    area: '',
    latitude: null,
    longitude: null,
    cuisine_type: '',
    currency: 'MYR',
    delivery_fee: '0',
    min_order_amount: '0',
    delivery_time_min: '30',
    preparation_time_min: '15',
    logo: '',
    cover_image: '',
    is_active: true,
    is_approved: true,
    approval_status: 'approved',
    featured: false,
    rating: 0,
    total_reviews: 0,
    order_count: 0,
    view_count: 0,
    commission_percent: 10
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const cuisineTypes = ['Restaurant', 'Pharmacy', 'Super market', 'Car', 'Home'];
  const currencies = ['EGP', 'USD', 'EUR', 'MYR', 'SAR'];

  useEffect(() => {
    fetchRestaurants();
    fetchStats();
  }, []);

  const fetchRestaurants = async () => {
    setLoading(true);
    try {
      const response = await api.get('/restaurants/admin/all');
      setRestaurants(response.data.restaurants || []);
    } catch (error) {
      console.error('Error fetching restaurants:', error);
      setMessage({
        type: 'error',
        text: language === 'ar' ? 'فشل في تحميل المطاعم' : 'Failed to load restaurants'
      });
    } finally {
      setLoading(false);
    }
  };

    // Handle auto-detect location
    const handleAutoDetect = () => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setSelectedLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
          setOpenMap(true);
        },
        (error) => {
          alert(language === 'ar' ? keywordsData.enable_gps_ar : keywordsData.enable_gps_en);
        }
      );
    };

  const fetchStats = async () => {
    try {
      const response = await api.get('/restaurants/admin/stats');
      setStats(response.data.stats);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleEditRestaurant = async (restaurant) => {
    setLoading(true);
    try {
      const response = await api.get(`/restaurants/${restaurant.id}`);
      const data = response.data.restaurant;
      setFormData({
        id: data.id,
        name: data.name || '',
        name_en: data.name_en || '',
        description: data.description || '',
        description_en: data.description_en || '',
        phone: data.phone || '',
        email: data.email || '',
        address: data.address || '',
        address_en: data.address_en || '',
        city: data.city || '',
        area: data.area || '',
        latitude: data.latitude || null,
        longitude: data.longitude || null,
        cuisine_type: data.cuisine_type || '',
        currency: data.currency || 'MYR',
        delivery_fee: data.delivery_fee || '0',
        min_order_amount: data.min_order_amount || '0',
        delivery_time_min: data.delivery_time_min || '30',
        preparation_time_min: data.preparation_time_min || '15',
        logo: data.logo || '',
        cover_image: data.cover_image || '',
        is_active: data.is_active || false,
        is_approved: data.is_approved || false,
        approval_status: data.approval_status || 'pending',
        featured: data.featured || false,
        rating: data.rating || 0,
        total_reviews: data.total_reviews || 0,
        order_count: data.order_count || 0,
        view_count: data.view_count || 0,
        commission_percent: data.commission_percent || 10
      });
      setEditDialogOpen(true);
    } catch (error) {
      console.error('Error fetching restaurant details:', error);
      setMessage({
        type: 'error',
        text: language === 'ar' ? 'فشل في تحميل تفاصيل المطعم' : 'Failed to load restaurant details'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRestaurant = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const submitData = {
        name: formData.name,
        name_en: formData.name_en,
        description: formData.description,
        description_en: formData.description_en,
        phone: formData.phone,
        email: formData.email,
        address: formData.address,
        address_en: formData.address_en,
        city: formData.city,
        area: formData.area,
        latitude: formData.latitude,
        longitude: formData.longitude,
        cuisine_type: formData.cuisine_type,
        currency: formData.currency,
        delivery_fee: formData.delivery_fee,
        min_order_amount: formData.min_order_amount,
        delivery_time_min: formData.delivery_time_min,
        preparation_time_min: formData.preparation_time_min,
        logo: formData.logo,
        cover_image: formData.cover_image,
        is_active: formData.is_active,
        is_approved: formData.is_approved,
        approval_status: formData.approval_status,
        featured: formData.featured,
        commission_percent: formData.commission_percent
      };

      await api.put(`/restaurants/admin/update/${formData.id}`, submitData);
      
      setMessage({
        type: 'success',
        text: language === 'ar' ? 'تم تحديث المطعم بنجاح' : 'Restaurant updated successfully'
      });
      
      setEditDialogOpen(false);
      fetchRestaurants();
      fetchStats();
    } catch (error) {
      console.error('Error updating restaurant:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || (language === 'ar' ? 'فشل في تحديث المطعم' : 'Failed to update restaurant')
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteRestaurant = async () => {
    if (!restaurantToDelete) return;

    setLoading(true);
    try {
      await api.delete(`/restaurants/admin/delete/${restaurantToDelete.id}`);
      
      setMessage({
        type: 'success',
        text: language === 'ar' ? 'تم حذف المطعم بنجاح' : 'Restaurant deleted successfully'
      });
      
      setDeleteDialogOpen(false);
      setRestaurantToDelete(null);
      fetchRestaurants();
      fetchStats();
    } catch (error) {
      console.error('Error deleting restaurant:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || (language === 'ar' ? 'فشل في حذف المطعم' : 'Failed to delete restaurant')
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApproveRestaurant = async (restaurantId, approved) => {
    setLoading(true);
    try {
      await api.put(`/restaurants/admin/approve/${restaurantId}`, {
        status: approved ? 'approved' : 'rejected'
      });
      
      setMessage({
        type: 'success',
        text: approved 
          ? (language === 'ar' ? 'تم قبول المطعم بنجاح' : 'Restaurant approved successfully')
          : (language === 'ar' ? 'تم رفض المطعم' : 'Restaurant rejected')
      });
      
      fetchRestaurants();
      fetchStats();
    } catch (error) {
      console.error('Error approving restaurant:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.message || (language === 'ar' ? 'فشل في معالجة الطلب' : 'Failed to process request')
      });
    } finally {
      setLoading(false);
    }
  };

    // Google Maps API
  const loadGoogleMapsAPI = (callback) => {
    if (window.google && window.google.maps) {
      callback();
      return;
    }
  
    if (!document.getElementById("google-maps-script")) {
      const script = document.createElement("script");
      const api = 'AIzaSyBl9P55wDymA5tQzFgWFaZWgMyQMdOSWXA';
      script.id = "google-maps-script";
      script.src = `https://maps.googleapis.com/maps/api/js?key=${api}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        console.log("Google Maps API Loaded");
        callback();
      };
      script.onerror = () => {
        console.error("Failed to load Google Maps API.");
      };
      document.body.appendChild(script);
    } else {
      setTimeout(() => {
        if (window.google && window.google.maps) {
          callback();
        }
      }, 1000);
    }
  };


  const handleLocationSelect = (location) => {
    if (!window.google || !window.google.maps) {
      loadGoogleMapsAPI(() => handleLocationSelect(location));
      return;
    }
    setSelectedLocation(location);
    setFormData(prev => ({
      ...prev,
      latitude: location.lat,
      longitude: location.lng,
      address: location.address || prev.address
    }));
  };

  const handleImageChange = (field) => (imageUrl) => {
    setFormData(prev => ({ ...prev, [field]: imageUrl }));
    if (touched[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    if (touched[field]) {
      setErrors(prev => ({ ...prev, [field]: validateField(field, value) }));
    }
  };

  const handleSwitchChange = (field) => (event) => {
    setFormData(prev => ({ ...prev, [field]: event.target.checked }));
  };

  const validateField = (field, value) => {
    switch (field) {
      case 'name':
        if (!value) return language === 'ar' ? 'اسم المتجر مطلوب' : 'Store name is required';
        if (value.length < 3) return language === 'ar' ? 'اسم المتجر يجب أن يكون 3 أحرف على الأقل' : 'Store name must be at least 3 characters';
        return '';
      case 'phone':
        if (!value) return language === 'ar' ? 'رقم الهاتف مطلوب' : 'Phone number is required';
        const cleanPhone = value.replace(/[\s\-\(\)\.]/g, '');
        const phoneRegex = /^(\+?[0-9]{1,3})?[0-9]{7,12}$/;
        if (!phoneRegex.test(cleanPhone)) {
          return language === 'ar' 
            ? 'الرجاء إدخال رقم هاتف صالح (مثال: 0123456789 أو +20123456789)' 
            : 'Please enter a valid phone number (e.g., 0123456789 or +20123456789)';
        }
        const digitCount = cleanPhone.replace(/\+/g, '').length;
        if (digitCount < 7 || digitCount > 15) {
          return language === 'ar' 
            ? 'رقم الهاتف يجب أن يحتوي على 7-15 رقم' 
            : 'Phone number must contain 7-15 digits';
        }
        return '';
      case 'email':
        if (!value) return language === 'ar' ? 'البريد الإلكتروني مطلوب' : 'Email is required';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) return language === 'ar' ? 'يرجى إدخال عنوان بريد إلكتروني صالح' : 'Please enter a valid email address';
        return '';
      case 'address':
        if (!value) return language === 'ar' ? 'العنوان مطلوب' : 'Address is required';
        return '';
      case 'city':
        if (!value) return language === 'ar' ? 'المدينة مطلوبة' : 'City is required';
        return '';
      case 'latitude':
        if (!value) return language === 'ar' ? 'يرجى اختيار موقع المتجر على الخريطة' : 'Please select store location on map';
        return '';
      case 'commission_percent':
        if (value < 0 || value > 50) {
          return language === 'ar' ? 'نسبة العمولة يجب أن تكون بين 0 و 50' : 'Commission must be between 0 and 50';
        }
        return '';
      default:
        return '';
    }
  };

  const handleBlur = (field) => () => {
    setTouched(prev => ({ ...prev, [field]: true }));
    setErrors(prev => ({ ...prev, [field]: validateField(field, formData[field]) }));
  };

  const validateForm = () => {
    const newTouched = {};
    const newErrors = {};
    const requiredFields = ['name', 'phone', 'email', 'address', 'city', 'latitude'];
    
    requiredFields.forEach(field => {
      newTouched[field] = true;
      newErrors[field] = validateField(field, formData[field]);
    });
    
    setTouched(newTouched);
    setErrors(newErrors);
    
    return !Object.values(newErrors).some(error => error);
  };

  const filteredRestaurants = restaurants.filter(restaurant => {
    const matchesSearch = restaurant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (restaurant.name_en && restaurant.name_en.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = filterStatus === 'all' || restaurant.approval_status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const getStatusChip = (status) => {
    switch (status) {
      case 'approved':
        return <Chip label={language === 'ar' ? 'مقبول' : 'Approved'} color="success" size="small" />;
      case 'pending':
        return <Chip label={language === 'ar' ? 'قيد المراجعة' : 'Pending'} color="warning" size="small" />;
      case 'rejected':
        return <Chip label={language === 'ar' ? 'مرفوض' : 'Rejected'} color="error" size="small" />;
      default:
        return <Chip label={status} size="small" />;
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4,mb:10, backgroundColor: '#000000', minHeight: '100vh', color: '#FFFFFF' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <StorefrontIcon sx={{ fontSize: 48, color: '#FFD700' }} />
          <Typography variant="h3" sx={{ color: '#FFD700', fontWeight: 900, letterSpacing: '2px' }}>
            {language === 'ar' ? 'إدارة المطاعم' : 'Restaurant Management'}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={fetchRestaurants}
          sx={{
            backgroundColor: '#FFD700',
            color: '#000000',
            '&:hover': { backgroundColor: '#FFE55C' }
          }}
        >
          {language === 'ar' ? 'تحديث' : 'Refresh'}
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 3, backgroundColor: '#0D0D0D', border: '1px solid #2D2D2D', borderRadius: '16px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h4" sx={{ color: '#FFD700', fontWeight: 700 }}>{stats.total}</Typography>
                <Typography sx={{ color: '#B3B3B3' }}>{language === 'ar' ? 'إجمالي المطاعم' : 'Total Restaurants'}</Typography>
              </Box>
              <StorefrontIcon sx={{ fontSize: 48, color: '#FFD700', opacity: 0.7 }} />
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 3, backgroundColor: '#0D0D0D', border: '1px solid #2D2D2D', borderRadius: '16px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h4" sx={{ color: '#FF9800', fontWeight: 700 }}>{stats.pending}</Typography>
                <Typography sx={{ color: '#B3B3B3' }}>{language === 'ar' ? 'قيد المراجعة' : 'Pending'}</Typography>
              </Box>
              <AccessTimeIcon sx={{ fontSize: 48, color: '#FF9800', opacity: 0.7 }} />
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 3, backgroundColor: '#0D0D0D', border: '1px solid #2D2D2D', borderRadius: '16px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h4" sx={{ color: '#4CAF50', fontWeight: 700 }}>{stats.approved}</Typography>
                <Typography sx={{ color: '#B3B3B3' }}>{language === 'ar' ? 'مقبول' : 'Approved'}</Typography>
              </Box>
              <CheckCircleIcon sx={{ fontSize: 48, color: '#4CAF50', opacity: 0.7 }} />
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 3, backgroundColor: '#0D0D0D', border: '1px solid #2D2D2D', borderRadius: '16px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="h4" sx={{ color: '#F44336', fontWeight: 700 }}>{stats.rejected}</Typography>
                <Typography sx={{ color: '#B3B3B3' }}>{language === 'ar' ? 'مرفوض' : 'Rejected'}</Typography>
              </Box>
              <CancelIcon sx={{ fontSize: 48, color: '#F44336', opacity: 0.7 }} />
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {message.text && (
        <Alert 
          severity={message.type} 
          sx={{ mb: 3, backgroundColor: message.type === 'success' ? 'rgba(255, 215, 0, 0.1)' : 'rgba(255, 61, 0, 0.1)', color: message.type === 'success' ? '#FFD700' : '#FF3D00', border: `1px solid ${message.type === 'success' ? '#FFD700' : '#FF3D00'}`, borderRadius: '12px' }}
        >
          {message.text}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 4, backgroundColor: '#0D0D0D', border: '1px solid #2D2D2D', borderRadius: '16px' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder={language === 'ar' ? 'بحث عن مطعم...' : 'Search restaurant...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#B3B3B3' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#1A1A1A',
                  color: '#FFFFFF',
                  '& fieldset': { borderColor: '#2D2D2D' },
                  '&:hover fieldset': { borderColor: '#FFD700' },
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel sx={{ color: '#B3B3B3' }}>{language === 'ar' ? 'الحالة' : 'Status'}</InputLabel>
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                label={language === 'ar' ? 'الحالة' : 'Status'}
                sx={{
                  backgroundColor: '#1A1A1A',
                  color: '#FFFFFF',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#2D2D2D' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#FFD700' },
                }}
              >
                <MenuItem value="all">{language === 'ar' ? 'الكل' : 'All'}</MenuItem>
                <MenuItem value="approved">{language === 'ar' ? 'مقبول' : 'Approved'}</MenuItem>
                <MenuItem value="pending">{language === 'ar' ? 'قيد المراجعة' : 'Pending'}</MenuItem>
                <MenuItem value="rejected">{language === 'ar' ? 'مرفوض' : 'Rejected'}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Restaurants Table */}
      <Paper sx={{ backgroundColor: '#0D0D0D', border: '1px solid #2D2D2D', borderRadius: '16px', overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#1A1A1A' }}>
                <TableCell sx={{ color: '#FFD700', fontWeight: 700 }}>{language === 'ar' ? 'الصورة' : 'Image'}</TableCell>
                <TableCell sx={{ color: '#FFD700', fontWeight: 700 }}>{language === 'ar' ? 'الاسم' : 'Name'}</TableCell>
                <TableCell sx={{ color: '#FFD700', fontWeight: 700 }}>{language === 'ar' ? 'الهاتف' : 'Phone'}</TableCell>
                <TableCell sx={{ color: '#FFD700', fontWeight: 700 }}>{language === 'ar' ? 'المدينة' : 'City'}</TableCell>
                <TableCell sx={{ color: '#FFD700', fontWeight: 700 }}>{language === 'ar' ? 'النوع' : 'Type'}</TableCell>
                <TableCell sx={{ color: '#FFD700', fontWeight: 700 }}>{language === 'ar' ? 'الحالة' : 'Status'}</TableCell>
                <TableCell sx={{ color: '#FFD700', fontWeight: 700 }}>{language === 'ar' ? 'التقييم' : 'Rating'}</TableCell>
                <TableCell sx={{ color: '#FFD700', fontWeight: 700 }}>{language === 'ar' ? 'إجراءات' : 'Actions'}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress sx={{ color: '#FFD700' }} />
                  </TableCell>
                </TableRow>
              ) : filteredRestaurants.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4, color: '#B3B3B3' }}>
                    {language === 'ar' ? 'لا توجد مطاعم' : 'No restaurants found'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredRestaurants.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((restaurant) => (
                  <TableRow key={restaurant.id} sx={{ '&:hover': { backgroundColor: 'rgba(255, 215, 0, 0.05)' } }}>
                    <TableCell>
                      <Avatar src={restaurant.logo} sx={{ width: 50, height: 50, borderRadius: 2 }}>
                        <RestaurantIcon />
                      </Avatar>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ color: '#FFFFFF', fontWeight: 500 }}>{restaurant.name}</Typography>
                      {restaurant.name_en && (
                        <Typography variant="caption" sx={{ color: '#B3B3B3' }}>{restaurant.name_en}</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ color: '#FFFFFF' }}>{restaurant.phone}</TableCell>
                    <TableCell sx={{ color: '#FFFFFF' }}>{restaurant.city}</TableCell>
                    <TableCell>
                      <Chip label={restaurant.cuisine_type || '-'} size="small" sx={{ backgroundColor: 'rgba(255, 215, 0, 0.1)', color: '#FFD700' }} />
                    </TableCell>
                    <TableCell>{getStatusChip(restaurant.approval_status)}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Rating value={restaurant.rating || 0} readOnly size="small" />
                        <Typography variant="caption" sx={{ color: '#B3B3B3' }}>({restaurant.total_reviews || 0})</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title={language === 'ar' ? 'تعديل' : 'Edit'}>
                          <IconButton onClick={() => handleEditRestaurant(restaurant)} sx={{ color: '#FFD700' }}>
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        {restaurant.approval_status === 'pending' && (
                          <>
                            <Tooltip title={language === 'ar' ? 'قبول' : 'Approve'}>
                              <IconButton onClick={() => handleApproveRestaurant(restaurant.id, true)} sx={{ color: '#4CAF50' }}>
                                <CheckCircleIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={language === 'ar' ? 'رفض' : 'Reject'}>
                              <IconButton onClick={() => handleApproveRestaurant(restaurant.id, false)} sx={{ color: '#F44336' }}>
                                <CancelIcon />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        <Tooltip title={language === 'ar' ? 'حذف' : 'Delete'}>
                          <IconButton onClick={() => { setRestaurantToDelete(restaurant); setDeleteDialogOpen(true); }} sx={{ color: '#F44336' }}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredRestaurants.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          sx={{ color: '#B3B3B3', borderTop: '1px solid #2D2D2D' }}
        />
      </Paper>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ backgroundColor: '#0D0D0D', color: '#FFD700', borderBottom: '1px solid #2D2D2D' }}>
          {language === 'ar' ? 'تعديل بيانات المطعم' : 'Edit Restaurant'}
          <IconButton onClick={() => setEditDialogOpen(false)} sx={{ position: 'absolute', right: 8, top: 8, color: '#B3B3B3' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: '#0D0D0D', py: 3 }}>
          <Grid container spacing={2}>
            {/* Logo Upload */}
            <Grid item xs={12} md={6}>
              <Typography sx={{ color: '#B3B3B3', mb: 1 }}>{language === 'ar' ? 'شعار المتجر' : 'Store Logo'}</Typography>
              <ImageUpload
                value={formData.logo}
                onChange={handleImageChange('logo')}
                productName={formData.name}
                language={language}
                noTitle={true}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography sx={{ color: '#B3B3B3', mb: 1 }}>{language === 'ar' ? 'صورة الغلاف' : 'Cover Image'}</Typography>
              <ImageUpload
                value={formData.cover_image}
                onChange={handleImageChange('cover_image')}
                productName={formData.name}
                language={language}
                noTitle={true}
              />
            </Grid>

            {/* Basic Info */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={language === 'ar' ? 'اسم المتجر (عربي)' : 'Store Name (Arabic)'}
                value={formData.name}
                onChange={handleChange('name')}
                onBlur={handleBlur('name')}
                error={touched.name && !!errors.name}
                helperText={touched.name && errors.name}
                sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#1A1A1A', color: '#FFFFFF', '& fieldset': { borderColor: '#2D2D2D' }, '&:hover fieldset': { borderColor: '#FFD700' } } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={language === 'ar' ? 'اسم المتجر (إنجليزي)' : 'Store Name (English)'}
                value={formData.name_en}
                onChange={handleChange('name_en')}
                sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#1A1A1A', color: '#FFFFFF', '& fieldset': { borderColor: '#2D2D2D' }, '&:hover fieldset': { borderColor: '#FFD700' } } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={language === 'ar' ? 'رقم الهاتف' : 'Phone'}
                value={formData.phone}
                onChange={handleChange('phone')}
                onBlur={handleBlur('phone')}
                error={touched.phone && !!errors.phone}
                helperText={touched.phone && errors.phone}
                sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#1A1A1A', color: '#FFFFFF', '& fieldset': { borderColor: '#2D2D2D' }, '&:hover fieldset': { borderColor: '#FFD700' } } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={language === 'ar' ? 'البريد الإلكتروني' : 'Email'}
                type="email"
                value={formData.email}
                onChange={handleChange('email')}
                onBlur={handleBlur('email')}
                error={touched.email && !!errors.email}
                helperText={touched.email && errors.email}
                sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#1A1A1A', color: '#FFFFFF', '& fieldset': { borderColor: '#2D2D2D' }, '&:hover fieldset': { borderColor: '#FFD700' } } }}
              />
            </Grid>

            {/* Location */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={language === 'ar' ? 'المدينة' : 'City'}
                value={formData.city}
                onChange={handleChange('city')}
                onBlur={handleBlur('city')}
                error={touched.city && !!errors.city}
                helperText={touched.city && errors.city}
                sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#1A1A1A', color: '#FFFFFF', '& fieldset': { borderColor: '#2D2D2D' }, '&:hover fieldset': { borderColor: '#FFD700' } } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={language === 'ar' ? 'المنطقة' : 'Area'}
                value={formData.area}
                onChange={handleChange('area')}
                sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#1A1A1A', color: '#FFFFFF', '& fieldset': { borderColor: '#2D2D2D' }, '&:hover fieldset': { borderColor: '#FFD700' } } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label={language === 'ar' ? 'العنوان' : 'Address'}
                value={formData.address}
                onChange={handleChange('address')}
                onBlur={handleBlur('address')}
                error={touched.address && !!errors.address}
                helperText={touched.address && errors.address}
                sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#1A1A1A', color: '#FFFFFF', '& fieldset': { borderColor: '#2D2D2D' }, '&:hover fieldset': { borderColor: '#FFD700' } } }}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleAutoDetect}
                startIcon={<MapIcon />}
                sx={{ borderColor: '#FFD700', color: '#FFD700', '&:hover': { borderColor: '#FFE55C', backgroundColor: 'rgba(255, 215, 0, 0.1)' } }}
              >
                {formData.latitude 
                  ? `${language === 'ar' ? 'تم اختيار الموقع' : 'Location Selected'} (${formData.latitude}, ${formData.longitude})`
                  : language === 'ar' ? 'اختر موقع المتجر على الخريطة' : 'Select store location on map'}
              </Button>
            </Grid>

            {/* Settings */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#B3B3B3' }}>{language === 'ar' ? 'نوع المتجر' : 'Store Type'}</InputLabel>
                <Select
                  value={formData.cuisine_type}
                  onChange={handleChange('cuisine_type')}
                  label={language === 'ar' ? 'نوع المتجر' : 'Store Type'}
                  sx={{ backgroundColor: '#1A1A1A', color: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#2D2D2D' } }}
                >
                  {cuisineTypes.map(type => <MenuItem key={type} value={type}>{type}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#B3B3B3' }}>{language === 'ar' ? 'العملة' : 'Currency'}</InputLabel>
                <Select
                  value={formData.currency}
                  onChange={handleChange('currency')}
                  label={language === 'ar' ? 'العملة' : 'Currency'}
                  sx={{ backgroundColor: '#1A1A1A', color: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#2D2D2D' } }}
                >
                  {currencies.map(type => <MenuItem key={type} value={type}>{type}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label={language === 'ar' ? 'رسوم التوصيل' : 'Delivery Fee'}
                type="number"
                value={formData.delivery_fee}
                onChange={handleChange('delivery_fee')}
                InputProps={{ startAdornment: <InputAdornment position="start"><AttachMoneyIcon /></InputAdornment> }}
                sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#1A1A1A', color: '#FFFFFF', '& fieldset': { borderColor: '#2D2D2D' } } }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label={language === 'ar' ? 'الحد الأدنى للطلب' : 'Min Order'}
                type="number"
                value={formData.min_order_amount}
                onChange={handleChange('min_order_amount')}
                InputProps={{ startAdornment: <InputAdornment position="start"><AttachMoneyIcon /></InputAdornment> }}
                sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#1A1A1A', color: '#FFFFFF', '& fieldset': { borderColor: '#2D2D2D' } } }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label={language === 'ar' ? 'وقت التوصيل (دقائق)' : 'Delivery Time (min)'}
                type="number"
                value={formData.delivery_time_min}
                onChange={handleChange('delivery_time_min')}
                sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#1A1A1A', color: '#FFFFFF', '& fieldset': { borderColor: '#2D2D2D' } } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={language === 'ar' ? 'نسبة العمولة (%)' : 'Commission (%)'}
                type="number"
                value={formData.commission_percent}
                onChange={handleChange('commission_percent')}
                onBlur={handleBlur('commission_percent')}
                error={touched.commission_percent && !!errors.commission_percent}
                helperText={touched.commission_percent && errors.commission_percent}
                sx={{ '& .MuiOutlinedInput-root': { backgroundColor: '#1A1A1A', color: '#FFFFFF', '& fieldset': { borderColor: '#2D2D2D' } } }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#B3B3B3' }}>{language === 'ar' ? 'حالة الموافقة' : 'Approval Status'}</InputLabel>
                <Select
                  value={formData.approval_status}
                  onChange={handleChange('approval_status')}
                  label={language === 'ar' ? 'حالة الموافقة' : 'Approval Status'}
                  sx={{ backgroundColor: '#1A1A1A', color: '#FFFFFF', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#2D2D2D' } }}
                >
                  <MenuItem value="approved">{language === 'ar' ? 'مقبول' : 'Approved'}</MenuItem>
                  <MenuItem value="pending">{language === 'ar' ? 'قيد المراجعة' : 'Pending'}</MenuItem>
                  <MenuItem value="rejected">{language === 'ar' ? 'مرفوض' : 'Rejected'}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, backgroundColor: '#1A1A1A', borderRadius: '8px' }}>
                <Typography sx={{ color: '#B3B3B3' }}>{language === 'ar' ? 'نشط' : 'Active'}</Typography>
                <Switch checked={formData.is_active} onChange={handleSwitchChange('is_active')} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#FFD700' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#FFD700' } }} />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, backgroundColor: '#1A1A1A', borderRadius: '8px' }}>
                <Typography sx={{ color: '#B3B3B3' }}>{language === 'ar' ? 'مميز' : 'Featured'}</Typography>
                <Switch checked={formData.featured} onChange={handleSwitchChange('featured')} sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#FFD700' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#FFD700' } }} />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#0D0D0D', borderTop: '1px solid #2D2D2D', p: 2 }}>
          <Button onClick={() => setEditDialogOpen(false)} sx={{ color: '#B3B3B3' }}>{language === 'ar' ? 'إلغاء' : 'Cancel'}</Button>
          <Button onClick={handleUpdateRestaurant} variant="contained" disabled={loading} startIcon={<SaveIcon />} sx={{ backgroundColor: '#FFD700', color: '#000000', '&:hover': { backgroundColor: '#FFE55C' } }}>
            {loading ? <CircularProgress size={24} sx={{ color: '#000000' }} /> : (language === 'ar' ? 'حفظ' : 'Save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle sx={{ backgroundColor: '#0D0D0D', color: '#FFD700' }}>
          {language === 'ar' ? 'تأكيد الحذف' : 'Confirm Delete'}
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: '#0D0D0D' }}>
          <Typography sx={{ color: '#FFFFFF' }}>
            {language === 'ar' 
              ? `هل أنت متأكد من حذف المطعم "${restaurantToDelete?.name}"؟`
              : `Are you sure you want to delete "${restaurantToDelete?.name}"?`}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#0D0D0D', borderTop: '1px solid #2D2D2D' }}>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ color: '#B3B3B3' }}>{language === 'ar' ? 'إلغاء' : 'Cancel'}</Button>
          <Button onClick={handleDeleteRestaurant} variant="contained" color="error" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : (language === 'ar' ? 'حذف' : 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Google Maps Modal */}
      <GoogleMapModal
        open={openMap}
        handleClose={() => setOpenMap(false)}
        onLocationSelect={(location) => handleLocationSelect(location)}
        initialCenter={selectedLocation}
        language={language}
        keywordsData={keywordsData}
      />
    </Container>
  );
};

export default AdminMenuEdit;